// File validation utilities for upload size limits
export const FILE_SIZE_LIMITS = {
  AUDIO: 100 * 1024 * 1024, // 100MB in bytes
  VIDEO: 150 * 1024 * 1024, // 150MB in bytes
  // A creative brief is a deck or a one-pager. Kept in step with the server's
  // own ceiling (MAX_BRIEF_BYTES) so the client refuses before uploading rather
  // than after, and the two never disagree about what is acceptable.
  PDF: 10 * 1024 * 1024, // 10MB in bytes
} as const;

export type FileType = "audio" | "video" | "pdf";

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates file size against the specified type limit
 */
export function validateFileSize(
  file: File,
  type: FileType
): FileValidationResult {
  const limit =
    type === "audio"
      ? FILE_SIZE_LIMITS.AUDIO
      : type === "pdf"
        ? FILE_SIZE_LIMITS.PDF
        : FILE_SIZE_LIMITS.VIDEO;
  const limitMB = limit / (1024 * 1024);
  const fileSizeMB = file.size / (1024 * 1024);

  if (file.size > limit) {
    return {
      isValid: false,
      error: `File size (${fileSizeMB.toFixed(
        1
      )}MB) exceeds the ${limitMB}MB limit for ${type} files.`,
    };
  }

  return { isValid: true };
}

/**
 * Validates multiple files and returns results for each
 */
export function validateFiles(
  files: File[],
  type: FileType
): Array<{ file: File; result: FileValidationResult }> {
  return files.map((file) => ({
    file,
    result: validateFileSize(file, type),
  }));
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Gets the appropriate file type from a file based on its MIME type
 */
export function getFileType(file: File): FileType | null {
  if (file.type.startsWith("audio/")) {
    return "audio";
  }
  if (file.type.startsWith("video/")) {
    return "video";
  }

  // Fallback to file extension
  const extension = file.name.split(".").pop()?.toLowerCase();
  const audioExtensions = ["mp3", "wav", "aiff", "m4a", "flac", "ogg", "aac"];
  const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];

  if (audioExtensions.includes(extension || "")) {
    return "audio";
  }
  if (videoExtensions.includes(extension || "")) {
    return "video";
  }

  return null;
}
